import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Tag } from "lucide-react";
import { api } from "../lib/api";
import { SECTORS, WIZARD_CONFIG } from "../data/wizardConfig";
import { toast } from "sonner";

export default function QuoteWizard() {
    const params = useParams();
    const sector = params.sector || "noleggio_lungo_termine";
    const [searchParams] = useSearchParams();
    const basedOnOfferId = searchParams.get("offer");
    const navigate = useNavigate();

    const steps = WIZARD_CONFIG[sector];
    const sectorInfo = SECTORS[sector];

    const [step, setStep] = useState(0); // 0..steps.length-1 wizard, steps.length = client info, steps.length+1 = review
    const [answers, setAnswers] = useState({});
    const [client, setClient] = useState({ name: "", phone: "", email: "" });
    const [offerCtx, setOfferCtx] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!steps) navigate("/nuovo-preventivo");
    }, [steps, navigate]);

    useEffect(() => {
        if (basedOnOfferId) {
            api.get(`/offers/${basedOnOfferId}`)
                .then(({ data }) => setOfferCtx(data))
                .catch(() => {});
        }
    }, [basedOnOfferId]);

    const totalSteps = steps ? steps.length + 2 : 0; // +client +review
    const progress = totalSteps ? Math.round(((step + 1) / totalSteps) * 100) : 0;

    const currentStepConfig = steps && step < steps.length ? steps[step] : null;
    const isClientStep = steps && step === steps.length;
    const isReviewStep = steps && step === steps.length + 1;

    const canNext = useMemo(() => {
        if (!steps) return false;
        if (isReviewStep) return true;
        if (isClientStep) return client.name.trim().length > 1;
        return (currentStepConfig?.fields || []).every((f) => {
            if (!f.required) return true;
            const v = answers[f.id];
            if (f.type === "multi") return Array.isArray(v) && v.length > 0;
            return v && String(v).trim().length > 0;
        });
    }, [steps, currentStepConfig, answers, client, isClientStep, isReviewStep]);

    if (!steps) return null;

    const setField = (id, value) =>
        setAnswers((prev) => ({ ...prev, [id]: value }));

    const toggleMulti = (id, opt) => {
        const prev = answers[id] || [];
        setAnswers({
            ...answers,
            [id]: prev.includes(opt) ? prev.filter((x) => x !== opt) : [...prev, opt],
        });
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post("/quotes", {
                sector,
                client_name: client.name,
                client_phone: client.phone || null,
                client_email: client.email || null,
                based_on_offer_id: basedOnOfferId,
                answers,
            });
            toast.success("Richiesta inviata allo staff Scintilla Hub");
            navigate(`/richieste/${data.id}`);
        } catch (e) {
            toast.error("Errore durante l'invio. Riprova.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto" data-testid="quote-wizard">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <Link to="/nuovo-preventivo" className="btn-ghost" data-testid="wizard-back">
                    <ArrowLeft className="w-4 h-4" /> Cambia settore
                </Link>
                <div className="text-xs font-bold text-[#898A8D] tracking-widest">
                    STEP {step + 1} / {totalSteps}
                </div>
            </div>

            <div className="mb-2 flex items-center gap-2">
                <div className="text-xs tracking-widest font-bold text-[#898A8D]">
                    {sectorInfo.label.toUpperCase()}
                </div>
                {offerCtx && (
                    <span className="inline-flex items-center gap-1 bg-[#C7FF2C]/20 text-[#0C4650] px-2.5 py-1 rounded-full text-xs font-bold">
                        <Tag className="w-3 h-3" /> Basato su: {offerCtx.title}
                    </span>
                )}
            </div>

            {/* Progress */}
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-8">
                <div
                    className="h-full bg-[#C7FF2C] transition-all"
                    style={{ width: `${progress}%` }}
                    data-testid="wizard-progress"
                />
            </div>

            <div className="card p-6 sm:p-10 fade-up" key={step}>
                {currentStepConfig && (
                    <>
                        <h2 className="text-2xl sm:text-3xl font-black text-[#0C4650]">
                            {currentStepConfig.title}
                        </h2>
                        <div className="mt-6 space-y-6">
                            {currentStepConfig.fields.map((f) => (
                                <Field
                                    key={f.id}
                                    field={f}
                                    value={answers[f.id]}
                                    onSet={(v) => setField(f.id, v)}
                                    onToggle={(opt) => toggleMulti(f.id, opt)}
                                />
                            ))}
                        </div>
                    </>
                )}

                {isClientStep && (
                    <>
                        <h2 className="text-2xl sm:text-3xl font-black text-[#0C4650]">
                            Dati del cliente finale
                        </h2>
                        <p className="text-gray-600 mt-1">
                            A chi è destinato il preventivo?
                        </p>
                        <div className="mt-6 space-y-4">
                            <div>
                                <label className="label">Nome cliente *</label>
                                <input
                                    className="field"
                                    value={client.name}
                                    onChange={(e) =>
                                        setClient({ ...client, name: e.target.value })
                                    }
                                    data-testid="client-name"
                                />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label">Telefono</label>
                                    <input
                                        className="field"
                                        value={client.phone}
                                        onChange={(e) =>
                                            setClient({ ...client, phone: e.target.value })
                                        }
                                        data-testid="client-phone"
                                        placeholder="+39 ..."
                                    />
                                </div>
                                <div>
                                    <label className="label">Email</label>
                                    <input
                                        type="email"
                                        className="field"
                                        value={client.email}
                                        onChange={(e) =>
                                            setClient({ ...client, email: e.target.value })
                                        }
                                        data-testid="client-email"
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {isReviewStep && (
                    <>
                        <h2 className="text-2xl sm:text-3xl font-black text-[#0C4650]">
                            Riepilogo e invio
                        </h2>
                        <p className="text-gray-600 mt-1">
                            Controlla tutto. Al click di "Invia" la richiesta viene presa in carico
                            dalla sede.
                        </p>

                        <div className="mt-6 bg-[#f7f7f5] rounded-xl p-5">
                            <div className="text-xs font-bold tracking-widest text-[#898A8D]">
                                CLIENTE
                            </div>
                            <div className="mt-1 font-bold text-[#0C4650] text-lg">
                                {client.name}
                            </div>
                            <div className="text-sm text-gray-600">
                                {[client.phone, client.email].filter(Boolean).join(" · ")}
                            </div>
                        </div>

                        {steps.map((s, idx) => (
                            <div key={s.title || idx} className="mt-4 border-t border-gray-100 pt-4">
                                <div className="text-xs font-bold tracking-widest text-[#898A8D]">
                                    {s.title.toUpperCase()}
                                </div>
                                <div className="mt-2 space-y-2">
                                    {s.fields.map((f) => {
                                        const v = answers[f.id];
                                        if (v == null || v === "" || (Array.isArray(v) && v.length === 0))
                                            return null;
                                        return (
                                            <div
                                                key={f.id}
                                                className="flex flex-col sm:flex-row sm:justify-between gap-1 text-sm"
                                            >
                                                <span className="text-gray-500">{f.label}</span>
                                                <span className="text-[#0C4650] font-semibold text-right">
                                                    {Array.isArray(v) ? v.join(", ") : v}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>

            <div className="mt-6 flex items-center justify-between">
                <button
                    onClick={() => setStep(Math.max(0, step - 1))}
                    disabled={step === 0}
                    className="btn-ghost disabled:opacity-30"
                    data-testid="wizard-prev"
                >
                    <ArrowLeft className="w-4 h-4" /> Indietro
                </button>
                {isReviewStep ? (
                    <button
                        onClick={submit}
                        disabled={submitting}
                        className="btn-primary"
                        data-testid="wizard-submit"
                    >
                        {submitting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Check className="w-4 h-4" />
                        )}
                        Invia richiesta
                    </button>
                ) : (
                    <button
                        onClick={() => setStep(step + 1)}
                        disabled={!canNext}
                        className="btn-primary"
                        data-testid="wizard-next"
                    >
                        Avanti <ArrowRight className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}

const Field = ({ field, value, onSet, onToggle }) => {
    if (field.type === "text") {
        return (
            <div>
                <label className="label">
                    {field.label}
                    {field.required && " *"}
                </label>
                <input
                    className="field"
                    value={value || ""}
                    onChange={(e) => onSet(e.target.value)}
                    data-testid={`field-${field.id}`}
                />
            </div>
        );
    }
    if (field.type === "textarea") {
        return (
            <div>
                <label className="label">{field.label}</label>
                <textarea
                    className="field min-h-[120px]"
                    value={value || ""}
                    onChange={(e) => onSet(e.target.value)}
                    data-testid={`field-${field.id}`}
                />
            </div>
        );
    }
    if (field.type === "single") {
        return (
            <div>
                <label className="label">
                    {field.label}
                    {field.required && " *"}
                </label>
                <div className="grid sm:grid-cols-2 gap-2">
                    {field.options.map((opt) => {
                        const sel = value === opt;
                        return (
                            <button
                                type="button"
                                key={opt}
                                onClick={() => onSet(opt)}
                                data-testid={`field-${field.id}-${opt.replace(/\s+/g, "_")}`}
                                className={`text-left border-2 rounded-xl px-4 py-3 font-semibold transition-all ${
                                    sel
                                        ? "border-[#0C4650] bg-[#0C4650] text-white"
                                        : "border-gray-200 text-[#0C4650] hover:border-[#C7FF2C]"
                                }`}
                            >
                                {opt}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }
    if (field.type === "single_custom") {
        const isPreset = field.options.includes(value);
        const customValue = !isPreset && value ? value : "";
        return (
            <div>
                <label className="label">
                    {field.label}
                    {field.required && " *"}
                </label>
                <div className="grid sm:grid-cols-2 gap-2">
                    {field.options.map((opt) => {
                        const sel = value === opt;
                        return (
                            <button
                                type="button"
                                key={opt}
                                onClick={() => onSet(opt)}
                                data-testid={`field-${field.id}-${opt.replace(/\s+/g, "_")}`}
                                className={`text-left border-2 rounded-xl px-4 py-3 font-semibold transition-all ${
                                    sel
                                        ? "border-[#0C4650] bg-[#0C4650] text-white"
                                        : "border-gray-200 text-[#0C4650] hover:border-[#C7FF2C]"
                                }`}
                            >
                                {opt}
                            </button>
                        );
                    })}
                </div>
                <input
                    className="field mt-3"
                    placeholder={field.custom_placeholder || "Altro (specifica)"}
                    value={customValue}
                    onChange={(e) => onSet(e.target.value)}
                    data-testid={`field-${field.id}-custom`}
                />
            </div>
        );
    }
    if (field.type === "anticipo") {
        const isZero = value === "Zero anticipo";
        const amount = value && !isZero ? value.replace(/^€\s*/, "") : "";
        return (
            <div>
                <label className="label">
                    {field.label}
                    {field.required && " *"}
                </label>
                <div className="grid sm:grid-cols-2 gap-2">
                    <button
                        type="button"
                        onClick={() => onSet("Zero anticipo")}
                        data-testid={`field-${field.id}-zero`}
                        className={`text-left border-2 rounded-xl px-4 py-3 font-semibold transition-all ${
                            isZero
                                ? "border-[#0C4650] bg-[#0C4650] text-white"
                                : "border-gray-200 text-[#0C4650] hover:border-[#C7FF2C]"
                        }`}
                    >
                        Zero anticipo
                    </button>
                    <div
                        className={`flex items-center border-2 rounded-xl px-3 transition-all ${
                            !isZero && amount
                                ? "border-[#0C4650] bg-white"
                                : "border-gray-200 bg-white"
                        }`}
                    >
                        <span className="text-[#898A8D] font-semibold mr-2">€</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            placeholder="Importo anticipo"
                            className="flex-1 py-3 bg-transparent focus:outline-none font-semibold text-[#0C4650]"
                            value={amount}
                            onChange={(e) => {
                                const v = e.target.value.trim();
                                onSet(v ? `€ ${v}` : "");
                            }}
                            data-testid={`field-${field.id}-amount`}
                        />
                    </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    Seleziona "Zero anticipo" oppure indica l'importo dell'anticipo.
                </p>
            </div>
        );
    }
    if (field.type === "multi") {
        const selected = value || [];
        return (
            <div>
                <label className="label">
                    {field.label}
                    {field.required && " *"}
                </label>
                <div className="grid sm:grid-cols-2 gap-2">
                    {field.options.map((opt) => {
                        const sel = selected.includes(opt);
                        return (
                            <button
                                type="button"
                                key={opt}
                                onClick={() => onToggle(opt)}
                                data-testid={`field-${field.id}-${opt.replace(/\s+/g, "_")}`}
                                className={`text-left border-2 rounded-xl px-4 py-3 font-semibold transition-all flex items-center justify-between ${
                                    sel
                                        ? "border-[#C7FF2C] bg-[#C7FF2C] text-[#0C4650]"
                                        : "border-gray-200 text-[#0C4650] hover:border-[#91C6C0]"
                                }`}
                            >
                                <span>{opt}</span>
                                {sel && <Check className="w-4 h-4" />}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }
    return null;
};
